import type { ClientTheme } from '@/types/theme'
import { pickTenantLogoSrc, type TenantLogoConfig } from '@/hooks/use-tenant-logo-src'
import { TENANT_BRAND_ICON_NODES, type TenantBrandIconNode } from '@/lib/tenant-brand-icon-nodes'

export type ClientMarkConfig = TenantLogoConfig & {
  faviconPath?: string
  faviconStyle?: string
  brandIcon?: string
  theme?: ClientTheme
}

function iconNodeToSVG(iconNode: TenantBrandIconNode[]): string {
  const strokeAttrs =
    'stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"'
  const inner = iconNode
    .map(([tag, attrs]) => {
      const filtered = { ...attrs }
      delete filtered.key
      const attrStr = Object.entries(filtered)
        .map(([k, v]) => `${k}="${String(v)}"`)
        .join(' ')
      return `<${tag} ${attrStr} ${strokeAttrs}/>`
    })
    .join('')
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">${inner}</svg>`
}

function loadIconNode(iconName: string): string {
  const node = TENANT_BRAND_ICON_NODES[iconName] ?? TENANT_BRAND_ICON_NODES.Bot
  return iconNodeToSVG(node)
}

export function generateIconFavicon(iconName: string): string {
  const svg = loadIconNode(iconName)
  if (!svg) return '/favicon.svg'
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

export function generateTcBoxedFaviconForTheme(theme: ClientTheme | undefined, isDark: boolean): string {
  const primary = isDark
    ? (theme?.dark?.colors?.primary ?? '#3b82f6')
    : (theme?.colors?.primary ?? '#0B5CFF')
  const foreground = isDark
    ? (theme?.dark?.colors?.primaryForeground ?? '#ffffff')
    : (theme?.colors?.primaryForeground ?? '#ffffff')

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
  <rect fill="${primary}" width="32" height="32" rx="4"/>
  <text fill="${foreground}" x="16" y="18" font-family="system-ui,sans-serif" font-weight="800" font-size="20" text-anchor="middle" dominant-baseline="middle">TC</text>
</svg>`

  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

/** Tab favicon — uses prefers-color-scheme for browsers without app theme context. */
export function generateTcBoxedFavicon(theme?: ClientTheme): string {
  const lightPrimary = theme?.colors?.primary ?? '#0B5CFF'
  const lightForeground = theme?.colors?.primaryForeground ?? '#ffffff'
  const darkPrimary = theme?.dark?.colors?.primary ?? '#3b82f6'
  const darkForeground = theme?.dark?.colors?.primaryForeground ?? '#ffffff'

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
  <style>
    .box { fill: ${lightPrimary}; }
    .text { fill: ${lightForeground}; }
    @media (prefers-color-scheme: dark) {
      .box { fill: ${darkPrimary}; }
      .text { fill: ${darkForeground}; }
    }
  </style>
  <rect class="box" width="32" height="32" rx="4"/>
  <text class="text" x="16" y="18" font-family="system-ui,sans-serif" font-weight="800" font-size="20" text-anchor="middle" dominant-baseline="middle">TC</text>
</svg>`

  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

/** Resolve favicon href for document head (logo not used here — tab favicon only). */
export function resolveDocumentFaviconSrc(cfg: ClientMarkConfig): string {
  const faviconPath = cfg.faviconPath?.trim()
  if (faviconPath) return faviconPath
  if (cfg.faviconStyle === 'tc-boxed') return generateTcBoxedFavicon(cfg.theme)
  if (cfg.brandIcon) return generateIconFavicon(cfg.brandIcon)
  return '/favicon.svg'
}

/**
 * Agent avatar image src after logo (caller supplies theme-aware logo from useTenantLogoSrc).
 * Priority: faviconPath → generated mark → undefined (caller falls back to Lucide).
 */
export function resolveClientMarkSrcAfterLogo(
  cfg: ClientMarkConfig,
  logoSrc: string | undefined,
): string | undefined {
  if (logoSrc?.trim()) return logoSrc.trim()
  const faviconPath = cfg.faviconPath?.trim()
  if (faviconPath) return faviconPath
  if (cfg.faviconStyle === 'tc-boxed') return generateTcBoxedFavicon(cfg.theme)
  if (cfg.brandIcon) return generateIconFavicon(cfg.brandIcon)
  return undefined
}

/**
 * Chat agent avatar image src (logo supplied separately from useTenantLogoSrc).
 * Skips generateIconFavicon — caller falls back to Lucide brandIcon.
 */
export function resolveChatAgentMarkSrc(
  cfg: ClientMarkConfig,
  logoSrc: string | undefined,
  isDark: boolean,
): string | undefined {
  if (logoSrc?.trim()) return logoSrc.trim()
  const faviconPath = cfg.faviconPath?.trim()
  if (faviconPath) return faviconPath
  if (cfg.faviconStyle === 'tc-boxed') return generateTcBoxedFaviconForTheme(cfg.theme, isDark)
  return undefined
}

export { pickTenantLogoSrc }
